import { usePermissionsStore } from '~/stores/permissions';

export default defineNuxtRouteMiddleware(async (to) => {
  const auth = useAuth();
  const config = useRuntimeConfig();
  const authMode = (config.public as any).AUTH_MODE ?? "keycloak";

  // Public routes
  const publicPaths = ["/login", "/logout"];
  const isPublic =
    publicPaths.includes(to.path) ||
    to.path.startsWith("/auth") ||
    to.path.startsWith("/public");

  if (isPublic) return;

  // Ensure we have attempted to load session once
  if (!auth.user && !auth.loadingUser) {
    await auth.fetchUser();
  }

  if (!auth.loadingSessionProvider && auth.sessionProvider === null) {
    await auth.fetchSessionProvider();
  }

  if (!auth.user) {
    const accessToken = useCookie('accessToken');
    if (accessToken.value) {
      return;
    }
    const wantsKeycloak = auth.sessionProvider
      ? auth.sessionProvider === "keycloak"
      : authMode === "keycloak";
    // Only redirect to Keycloak if session indicates Keycloak
    if (wantsKeycloak) {
      // BFF login via backend
      auth.startKeycloakLogin(to.fullPath);
      return;
    }
    // For local auth mode, redirect to login page
    if (to.path !== "/login") {
      return navigateTo({ path: "/login", query: { returnTo: to.fullPath } });
    }
  }

  const permissionsStore = usePermissionsStore();
  // Fetch permissions if not loaded and user is available
  if (auth.user && !permissionsStore.loading && !permissionsStore.permissions.length) {
    await permissionsStore.fetchPermissions(auth.user.id);
  }

  // Wait for permissions to load if not already loaded
  if (permissionsStore.loading) {
    // Optionally show a loading spinner or block navigation
    await new Promise((resolve) => {
      const interval = setInterval(() => {
        if (!permissionsStore.loading) {
          clearInterval(interval);
          resolve(undefined);
        }
      }, 50);
    });
  }

  // Global wildcard: if user has write: or read:, allow all routes
  if (permissionsStore.permissions.includes('write:') || permissionsStore.permissions.includes('read:')) {
    return;
  }

  // Route meta permission enforcement
  const requiredPermissions = to.meta?.permissions;
  if (requiredPermissions && Array.isArray(requiredPermissions)) {
    const hasAll = requiredPermissions.every((perm) =>
      permissionsStore.permissions.includes(perm)
    );
    if (!hasAll) {
      // Redirect to first accessible route instead of unauthorized page
      const firstRoute = (permissionsStore as any).getFirstAccessibleRoute();
      if (firstRoute && firstRoute !== to.path) {
        return navigateTo(firstRoute);
      } else {
        return navigateTo('/');
      }
    }
  }

  // Users with only read:user:self cannot list users — redirect to their own profile
  const selfOnlyUser =
    !permissionsStore.canRead('user') &&
    permissionsStore.permissions.includes('read:user:self');

  // Route-level permission enforcement (dynamic, supports nested routes)
  const routePermissionPatterns: Array<{ pattern: RegExp, helper: () => boolean, redirectTo?: string }> = [
    { pattern: /^\/clients(\/|$)/, helper: permissionsStore.canViewClients || permissionsStore.canShowClientGroupsMenu },
    { pattern: /^\/clientGroups(\/|$)/, helper: permissionsStore.canShowClientGroupsMenu },
    { pattern: /^\/configProfile(\/|$)/, helper: permissionsStore.canShowConfigProfilesMenu },
    { pattern: /^\/users\/?$/, helper: permissionsStore.canShowUsersMenu, redirectTo: selfOnlyUser ? '/users/me' : undefined },
    { pattern: /^\/users(\/|$)/, helper: permissionsStore.canShowUsersMenu },
    { pattern: /^\/roles(\/|$)/, helper: permissionsStore.canShowRolesMenu },
    { pattern: /^\/userGroups(\/|$)/, helper: permissionsStore.canShowUserGroupsMenu },
    { pattern: /^\/audit-log(\/|$)/, helper: permissionsStore.canShowAuditLogMenu },
    { pattern: /^\/tenants(\/|$)/, helper: permissionsStore.canShowTenantsMenu },
    { pattern: /^\/settings(\/|$)/, helper: permissionsStore.canShowSettings },
  ];

  const matched = routePermissionPatterns.find(({ pattern }) => pattern.test(to.path));
  if (matched) {
    if (matched.redirectTo && matched.redirectTo !== to.path) {
      return navigateTo(matched.redirectTo);
    }
    if (!matched.helper()) {
      const firstRoute = (permissionsStore as any).getFirstAccessibleRoute();
      if (firstRoute && firstRoute !== to.path) {
        return navigateTo(firstRoute);
      } else {
        return navigateTo('/');
      }
    }
  }

  // Final check: if route is not accessible, redirect to first accessible route
  const firstRoute = permissionsStore.getFirstAccessibleRoute();
  if (firstRoute && firstRoute !== to.path && !matched?.helper()) {
    return navigateTo(firstRoute);
  }
});
